import { createSelector } from 'reselect';
import { roundValue } from '@ynomia/core/dist/utils';
import {
  getAssetManagerFilters,
  getAssetManagerSelectedAssetTypeId,
  getObservationTypes,
  getSelectedAssetType,
  getSummariesConfig,
} from '..';
import { getFilteredAssets, getFilteredAssetsContext } from './index';
import { ObservationSummaryConfig } from '../../config/types';
import map from '../map';

const getFilteredAssetsForObservationSummary = createSelector(
  [
    getAssetManagerFilters,
    getAssetManagerSelectedAssetTypeId,
    getFilteredAssetsContext,
  ],
  (
    assetManagerFilters,
    selectedAssetTypeId,
    filteredAssetsContext,
  ) => {
    const {
      searchTags,
      searchText,
      dateFilter,
      selectedLayerIds,
      assetStatusFilter,
      selectedBandIds,
      tableFilter,
      openedLayerId,
      multiSearchModeFilter,
      fieldFilters,
    } = assetManagerFilters;
    const filteredAssets = getFilteredAssets(filteredAssetsContext, {
      searchTags,
      searchText,
      dateFilter,
      selectedAssetTypeId,
      selectedBandIds,
      selectedLayerIds,
      assetStatusFilter,
      tableFilter,
      openedLayerId,
      multiSearchModeFilter,
      fieldFilters,
    });
    return filteredAssets.sort((a, b) => String(a.label).localeCompare(String(b.label)));
  },
);

export const getObservationSummaryConfigByAssetManagerId = createSelector(
  [map.features],
  (features): Map<string, { [key: string]: ObservationSummaryConfig }> => {
    type ObservationSummaryConfigMap = Map<string, { [key: string]: ObservationSummaryConfig }>;
    const observationSummaryByFeature: ObservationSummaryConfigMap = new Map();

    if (features) {
      const featuresKeys = Object.keys(features);
      featuresKeys?.forEach((key) => {
        const summaries = features[key]?.summaries || {};
        const observationSummaries = Object.keys(summaries).reduce((acc, assetType) => {
          const summaryList = summaries[assetType];
          acc[assetType] = summaryList.find(
            (summary: { type: string }) => summary.type === 'observation_summary',
          );
          return acc;
        }, {});
        observationSummaryByFeature.set(
          key,
          observationSummaries,
        );
      });
    }
    return observationSummaryByFeature;
  },
);

const getObservationsByObservationTypes = createSelector(
  [getFilteredAssetsForObservationSummary],
  (filteredAssetsForObservationSummary) => {
    const assetsByObservationType: Map<string, Array<string>> = new Map();

    filteredAssetsForObservationSummary.forEach((asset) => {
      const recordedTypes = new Set<string>();
      asset.observations?.forEach(({ type }) => {
        // Do not count same obs type multiple times
        if (recordedTypes.has(type)) return;
        recordedTypes.add(type);
        const assetList = assetsByObservationType.get(type);
        if (assetList) {
          assetList.push(asset.id);
        } else {
          assetsByObservationType.set(type, [asset.id]);
        }
      });
    });
    return assetsByObservationType;
  },
);

export const getObservationSummaryConfig = createSelector(
  [
    getSummariesConfig,
    getSelectedAssetType,
  ],
  (
    summariesConfig,
    selectedAssetType,
  ) => {
    if (!summariesConfig) return undefined;
    // TODO: Support multiple observation summary configurations
    return summariesConfig[selectedAssetType.id]?.find(
      summary => summary.type === 'observation_summary',
    ) as ObservationSummaryConfig;
  },
);

export const getObservationSummaryTitle = createSelector(
  [getObservationSummaryConfig],
  observationSummaryConfig => observationSummaryConfig?.title || 'Actions',
);

export const getObservationSummary = createSelector(
  [
    getObservationTypes,
    getFilteredAssetsForObservationSummary,
    getObservationsByObservationTypes,
    getObservationSummaryConfig,
  ],
  (
    observationTypes,
    filteredAssetsForObservationSummary,
    observationsByObservationTypes,
    observationSummaryTypes,
  ) => {
    const totalObservationAssets = filteredAssetsForObservationSummary.length;
    return observationTypes
      .filter(o => (!observationSummaryTypes?.observationTypes
        || observationSummaryTypes.observationTypes.includes(o.id)))
      .map((observationType) => {
        const { id } = observationType;
        const occurrence = observationsByObservationTypes.get(id)?.length || 0;
        const occurrencePercentage = roundValue(
          (occurrence * 100) / totalObservationAssets,
          1,
        );

        const noOccurrence = totalObservationAssets - occurrence;
        const noOccurencePercentage = roundValue(100 - occurrencePercentage, 1);
        return {
          ...observationType,
          occurrence,
          occurrencePercentage,
          noOccurrence,
          noOccurencePercentage,
        };
      });
  },
);
